import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { InputTextModule } from "primeng/inputtext";
import { PasswordModule } from "primeng/password";

import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import {
    startAuthentication,
    startRegistration,
} from '@simplewebauthn/browser';
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { catchError } from "rxjs";
import { ButtonComponent } from "../../@core/components/ui/button/button.component";
import { InputComponent } from "../../@core/components/ui/input/input.component";
import { AuthenticationService, ILogin, PRIMARY_AUTH_METHOD } from "../../@core/services/authentication.service";
import { ToastService } from "../../@core/services/toast.service";
import { LOGIN_WITH } from "../../@core/models/enums";
import { TooltipModule } from "primeng/tooltip";
import { LOGIN_METHOD } from "src/app/contract-management/models/contract-management.model";
import { TextCasePipe } from "../../@shared/pipes/textCase.pipe";
import { CaseType } from "src/app/@shared/services/case-format.service";
import { AssetUrlPipe } from "src/app/@shared/pipes/asset-url.pipe";

export enum AUTH_PROVIDER {
    GOOGLE = 'GOOGLE',
    MICROSOFT = 'MICROSOFT'
}

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        InputTextModule,
        PasswordModule,
        ButtonComponent,
        InputComponent,
        RouterModule,
        ProgressSpinnerModule,
        TooltipModule,
        TextCasePipe,
        AssetUrlPipe
    ],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LogInComponent implements OnInit, OnDestroy {

    loginForm!: FormGroup;

    mfaCode: string = '';
    otpCode: string = '';

    defaultUrl: string = ''

    CASE_TYPE = CaseType;

    currentAuthMethod: PRIMARY_AUTH_METHOD = PRIMARY_AUTH_METHOD.PASSWORD;
    LOGIN_METHOD = LOGIN_METHOD;

    mfaRegister: boolean = false;
    mfaVerify: boolean = false;

    mfaData !: {
        secret: string,
        qrCode: string
    }

    userInfo !: {
        email: string,
        name: string
    }

    otpAttemptCount: number = 0;
    maxOtpAttemptLimit: number = 5;
    emailSentForMaxAttempt: boolean = false;
    isOtpLoading: boolean = false;
    AUTH_PROVIDER = AUTH_PROVIDER
    PRIMARY_AUTH_METHODS = PRIMARY_AUTH_METHOD
    authCode: string = ""

    authProvider: string = ""

    error: string = ""

    tryAnotherWay: boolean = false;
    availableMFAMethods: PRIMARY_AUTH_METHOD[] = []

    thirdPartyAuthLoading: boolean = false

    resendTimer: number = 0;
    resendTimerInterval: any;
    canResendOTP: boolean = true;

    passkeyAuthError: boolean = false;
    LOGIN_WITH = LOGIN_WITH;
    loginWith: LOGIN_WITH = LOGIN_WITH.EMAIL;

    // Two-step login flow
    loginStep: number = 1; // 1 = identifier input, 2 = login method selection
    organizationLoginMethods: string[] = [];
    isLoadingLoginMethods: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private fb: FormBuilder,
        private toastService: ToastService,
        private authenticationService: AuthenticationService
    ) {
        const returnUrl = this.route.snapshot.queryParams['returnUrl'] || this.defaultUrl;
        localStorage.setItem('returnUrl', returnUrl);

        const parsedReturnUrl = this.parseReturnUrl(returnUrl);
        if (parsedReturnUrl?.pathname === '/general-agent' && parsedReturnUrl.searchParams.has('prompt')) {
            const prompt = parsedReturnUrl.searchParams.get('prompt') ?? '';
            localStorage.setItem('generalAgentPrompt', prompt);
        } else {
            localStorage.removeItem('generalAgentPrompt');
        }
    }

    get loginSentence(): string {
        const label =
            this.loginWith === LOGIN_WITH.EMAIL ? 'Username' : 'Email';

        return `Login with ${label}`;
    }

    get loginPlaceholder(): string {
        return this.loginWith === LOGIN_WITH.EMAIL ? 'Enter your email' : 'Enter your username';
    }

    ngOnInit() {
        this.loginForm = this.fb.group({
            identifier: ['', [Validators.required, Validators.email]],
            password: ['']
        });

        this.route.paramMap.subscribe(params => {
            this.authProvider = params.get('provider') ?? "";
            if (this.authProvider) {
                this.thirdPartyAuthLoading = true
                this.route.queryParamMap.subscribe(queryParams => {
                    this.authCode = queryParams.get('code') ?? '';
                    this.error = queryParams.get('error') ?? '';
                    if (this.authCode) {
                        this.completeAuthAndCheckMFA();
                    }
                    if (this.error) {
                        if (this.error == 'access_denied') {
                            this.toastService.error('User Denied Access', 'Error!');
                            this.router.navigate(['/login']);
                        }
                    }
                });
            }
        });
    }


    completeAuthAndCheckMFA() {
        switch (this.authProvider) {
            case 'google': {
                this.authenticationService.googleAuthConfirmation(this.authCode).pipe(catchError((error) => {
                    if (error) {
                        this.thirdPartyAuthLoading = false
                        this.router.navigate(['/login']);
                        return error
                    }
                })).subscribe((userLoggedIn) => {
                    if (userLoggedIn) {
                        this.thirdPartyAuthLoading = false
                        this.userInfo = {
                            email: userLoggedIn.email || '',
                            name: userLoggedIn.name || ''
                        }
                        this.currentAuthMethod = userLoggedIn.primaryAuthMethod;
                        this.switchToAuthMethod(userLoggedIn)
                    }
                })
                break;
            }
            case 'microsoft': {
                this.authenticationService.microsoftAuthConfirmation(this.authCode).pipe(catchError((error) => {
                    if (error) {
                        this.thirdPartyAuthLoading = false
                        this.router.navigate(['/login']);
                        return error
                    }
                })).subscribe((userLoggedIn) => {
                    if (userLoggedIn) {
                        this.thirdPartyAuthLoading = false
                        this.userInfo = {
                            email: userLoggedIn.email || '',
                            name: userLoggedIn.name || ''
                        }
                        this.currentAuthMethod = userLoggedIn.primaryAuthMethod;
                        this.switchToAuthMethod(userLoggedIn)
                    }
                })
                break;
            }



        }

    }

    onNext() {
        const identifierControl = this.loginForm.get('identifier');
        if (identifierControl && identifierControl.valid) {
            this.isLoadingLoginMethods = true;
            const identifier = identifierControl.value;

            this.authenticationService.verifyIdentifierAndGetLoginMethods(identifier, this.loginWith).subscribe((response) => {
                this.isLoadingLoginMethods = false;
                if (response.success && response.data) {
                    if (!response.data.userExists) {
                        this.toastService.error('User not found. Please check your email/username.');
                        return;
                    }

                    if (response.data.userDeactivated) {
                        this.toastService.error('User is deactivated. Please contact your administrator.');
                        return;
                    }

                    this.organizationLoginMethods = response.data.loginMethods || [];

                    if (this.organizationLoginMethods.length === 0) {
                        this.toastService.error('No login methods available for this account. Please contact your administrator.');
                        return;
                    }

                    // If PASSWORD is enabled, add password validator
                    if (this.organizationLoginMethods.includes(LOGIN_METHOD.PASSWORD)) {
                        this.loginForm.get('password')?.setValidators([Validators.required]);
                        this.loginForm.get('password')?.updateValueAndValidity();
                    }

                    this.loginStep = 2;
                } else {
                    this.toastService.error('Unable to verify user. Please try again.');
                }
            });
        } else {
            identifierControl?.markAsTouched();
        }
    }

    onBack() {
        this.loginStep = 1;
        this.organizationLoginMethods = [];
        this.loginForm.get('password')?.clearValidators();
        this.loginForm.get('password')?.setValue('');
        this.loginForm.get('password')?.updateValueAndValidity();
    }

    onSubmit() {
        if (this.loginForm.valid) {
            const credentials = this.loginForm.value;
            this.authenticationService.login({ loginWith: this.loginWith, ...credentials }).pipe(catchError((error) => {
                return error;
            })).subscribe((userLoggedIn) => {
                if (userLoggedIn) {
                    this.userInfo = {
                        email: userLoggedIn.email || '',
                        name: userLoggedIn.name || ''
                    }
                    this.currentAuthMethod = userLoggedIn.primaryAuthMethod;
                    this.switchToAuthMethod(userLoggedIn);
                }
                else {
                    this.toastService.error('Login Failed', 'Error!');
                }
            })
        }
    }

    hasLoginMethod(method: string): boolean {
        return this.organizationLoginMethods.includes(method);
    }

    switchToAuthMethod(userLoggedIn: ILogin) {
        switch (userLoggedIn.primaryAuthMethod) {
            case PRIMARY_AUTH_METHOD.MFA: {
                if (userLoggedIn.mfa_required) {
                    if (userLoggedIn.mfa_registration_required) {
                        this.mfaRegister = true;
                        this.mfaData = userLoggedIn.mfa_data || { secret: '', qrCode: '' };
                    }
                    else {
                        this.mfaVerify = true;
                    }
                }
                else {
                    this.navigateToReturnUrl();
                }
                break;
            }
            case PRIMARY_AUTH_METHOD.PASSKEYS: {
                if (userLoggedIn.passkeyRequired) {
                    if (userLoggedIn.passkeyRegistrationRequired) {
                        this.authenticationService.webAuthnRegisterOptions(this.userInfo.email).subscribe(async (res) => {
                            if (res) {
                                try {
                                    const attResp = await startRegistration(res.data);
                                    this.authenticationService.registerPasskey(this.userInfo.email, attResp).subscribe((response) => {
                                        if (response) {
                                            this.toastService.success('Passkey Registration Successful', 'Success!');
                                            this.router.navigate(['']);
                                        } else {
                                            this.toastService.error('Passkey Registration Failed', 'Error!');
                                        }
                                    });
                                } catch (error) {
                                    this.toastService.error('Passkey registration was cancelled', 'Cancelled');
                                    this.passkeyAuthError = true;
                                }
                            }
                        })

                    }
                    else {
                        this.authenticationService.webAuthnOptions(this.userInfo.email).subscribe(async (res) => {
                            if (res) {
                                try {
                                    const authResp = await startAuthentication(res.data);
                                    this.authenticationService.webAuthnPasskeyAuthenticate(this.userInfo.email, authResp).subscribe((response) => {
                                        if (response) {
                                            this.toastService.success('Passkey Login Successful', 'Success!');
                                            this.router.navigate(['']);
                                        } else {
                                            this.toastService.error('Passkey Login Failed', 'Error!');
                                        }
                                    });
                                } catch (error) {
                                    this.toastService.error('Passkey authentication was cancelled', 'Cancelled');
                                    this.passkeyAuthError = true;
                                }
                            }
                        })
                    }
                }
                break;
            }

            case PRIMARY_AUTH_METHOD.PASSWORD: {
                this.navigateToReturnUrl();
                break;
            }

            case PRIMARY_AUTH_METHOD.EMAIL_OTP: {
                this.authenticationService.sendTwoFactorEmailOtp(this.userInfo.email).subscribe((res) => {
                    if (res.success) {
                        this.toastService.success(res.message, 'Success!');
                        this.startResendTimer();
                    }
                    else {
                        this.toastService.error(res.message, 'Error !');
                    }
                })
                break;
            }

            case PRIMARY_AUTH_METHOD.SMS_OTP: {
                break;
            }
        }
    }

    redirectToSignup() {
        this.router.navigate(['/signup'])
    }

    completeRegistration() {
        this.authenticationService.completeMFARegistration(this.userInfo.email, this.mfaData.secret).subscribe((response) => {
            if (response) {
                this.toastService.success('MFA Registration Successful', 'Success!');
                this.mfaRegister = false;
                this.mfaVerify = true;
            } else {
                this.toastService.error('MFA Registration Failed', 'Error!');
            }
        });
    }

    verifyMFA() {
        const mfaCode = this.mfaCode.trim();
        this.authenticationService.verifyMFA(this.userInfo.email, mfaCode).subscribe((response) => {
            if (response) {
                this.toastService.success('MFA Verification Successful', 'Success!');
                this.navigateToReturnUrl();
            } else {
                this.toastService.error('MFA Verification Failed', 'Error!');
            }
        });
    }

    verifyOTP(otpcode: string) {
        if (this.otpAttemptCount >= this.maxOtpAttemptLimit - 1) {
            if (this.emailSentForMaxAttempt) {
                this.toastService.error("Max login attempts reached. Please try again later.");
                this.currentAuthMethod = PRIMARY_AUTH_METHOD.PASSWORD;
                this.router.navigate(['/login']);
                return;
            }

            const timestamp = new Date().toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short',
                timeZone: 'Asia/Kolkata'
            });

            this.toastService.error("Max login attempts reached. Try again after some time.");
            this.router.navigate(['/login']);

            this.authenticationService
                .sendUnusualActvityEmail(this.userInfo.email, this.userInfo.name, timestamp)
                .subscribe((res) => {
                    this.emailSentForMaxAttempt = true;
                    if (res) {
                        this.toastService.success("Security alert email sent successfully");
                        this.currentAuthMethod = PRIMARY_AUTH_METHOD.PASSWORD;
                        this.resetState();
                        this.router.navigate(['/login']);
                    } else {
                        this.toastService.error("Internal Server Error");
                        this.currentAuthMethod = PRIMARY_AUTH_METHOD.PASSWORD;
                        this.resetState();
                        this.router.navigate(['/login']);
                    }
                });

            return;
        }

        this.authenticationService.verifyTwoFactorOTP(this.userInfo.email, otpcode).subscribe((res) => {
            if (res.token) {
                this.toastService.success("OTP Verified Successfully")
                this.resetState();
                this.navigateToReturnUrl();
            }
            else {
                this.otpAttemptCount++;
                this.toastService.error("Invalid OTP entered , Try Again")
            }
        })
    }

    regenerateOTP() {
        if (this.isOtpLoading) {
            return;
        }

        if (!this.canResendOTP) {
            this.toastService.error(`Please wait ${this.resendTimer} seconds before requesting a new OTP`);
            return;
        }

        this.authenticationService.sendTwoFactorEmailOtp(this.userInfo.email).subscribe((res) => {
            if (res.success) {
                this.toastService.success(res.message, 'Success!');
                this.startResendTimer();
            }
            else {
                this.toastService.error(res.message, 'Error !');
            }
        })
    }

    startResendTimer() {
        this.canResendOTP = false;
        this.resendTimer = 30;

        this.resendTimerInterval = setInterval(() => {
            this.resendTimer--;
            if (this.resendTimer <= 0) {
                this.stopResendTimer();
            }
        }, 1000);
    }

    stopResendTimer() {
        if (this.resendTimerInterval) {
            clearInterval(this.resendTimerInterval);
            this.resendTimerInterval = null;
        }
        this.resendTimer = 0;
        this.canResendOTP = true;
    }

    ngOnDestroy() {
        this.stopResendTimer();
    }
    selectedAuthProvider!: AUTH_PROVIDER
    loginWithAuthProvider(provider: AUTH_PROVIDER) {
        this.thirdPartyAuthLoading = true
        this.selectedAuthProvider = provider
        switch (provider) {
            case AUTH_PROVIDER.GOOGLE: {
                this.authenticationService.googleAuthUrl().subscribe((res) => {
                    if (res && res.data) {
                        window.location.href = res.data;
                    }
                })
                break;
            }
            case AUTH_PROVIDER.MICROSOFT: {
                this.authenticationService.microsoftAuthUrl().subscribe((res) => {
                    if (res && res.data) {
                        window.location.href = res.data;
                    }
                })
            }

        }

    }

    tryAnotherWayFn() {
        this.tryAnotherWay = true;

        this.authenticationService.getAvailableMFAMethods(this.userInfo.email).subscribe((res) => {
            if (res.data) {
                this.availableMFAMethods = res.data
                this.availableMFAMethods = this.availableMFAMethods.filter(method => method !== this.currentAuthMethod)
                //Removing SMS OTP as it is not supported yet
                this.availableMFAMethods = this.availableMFAMethods.filter(method => method !== PRIMARY_AUTH_METHOD.SMS_OTP)
            }
        })
    }

    chooseMFAMethod(method: PRIMARY_AUTH_METHOD) {

        switch (method) {
            case PRIMARY_AUTH_METHOD.MFA: {
                this.tryAnotherWay = false
                this.mfaVerify = true;
                this.currentAuthMethod = method
                break;
            }
            case PRIMARY_AUTH_METHOD.PASSKEYS: {
                this.currentAuthMethod = method
                this.authenticationService.webAuthnOptions(this.userInfo.email).subscribe(async (res) => {
                    if (res) {

                        try {
                            const authResp = await startAuthentication(res.data);
                            this.authenticationService.webAuthnPasskeyAuthenticate(this.userInfo.email, authResp).subscribe((response) => {
                                if (response) {
                                    this.toastService.success('Passkey Login Successful', 'Success!');
                                    this.tryAnotherWay = false
                                    this.navigateToReturnUrl();
                                } else {
                                    this.toastService.error('Passkey Login Failed', 'Error!');
                                }
                            });
                        } catch (error) {
                            this.toastService.error('Passkey authentication was cancelled', 'Cancelled');
                            this.tryAnotherWay = true;
                        }
                    }
                })
                break;
            }
            case PRIMARY_AUTH_METHOD.EMAIL_OTP: {
                this.currentAuthMethod = method
                this.tryAnotherWay = false
                this.authenticationService.sendTwoFactorEmailOtp(this.userInfo.email).subscribe((res) => {
                    if (res.success) {
                        this.toastService.success(res.message, 'Success!');
                        this.startResendTimer();
                    }
                    else {
                        this.toastService.error(res.message, 'Error !');
                    }
                })
                break;
            }
            case PRIMARY_AUTH_METHOD.SMS_OTP: {
                break;
            }

        }
    }

    tryAgain() {
        this.resetState();
        this.loginForm.get('password')?.clearValidators();
        this.loginForm.get('password')?.setValue('');
        this.loginForm.get('password')?.updateValueAndValidity();
        this.loginForm.get('password')?.markAsPristine();
        this.passkeyAuthError = false;
        this.currentAuthMethod = PRIMARY_AUTH_METHOD.PASSWORD;
        this.router.navigate(['/login']);
    }

    resetState() {
        this.otpAttemptCount = 0;
        this.emailSentForMaxAttempt = false;
        this.passkeyAuthError = false;
        this.mfaVerify = false;
        this.mfaRegister = false;
        this.mfaData = { secret: '', qrCode: '' };
        this.userInfo = { email: '', name: '' };
        this.currentAuthMethod = PRIMARY_AUTH_METHOD.PASSWORD;
        this.tryAnotherWay = false;
        this.availableMFAMethods = [];
        this.thirdPartyAuthLoading = false;
        this.resendTimer = 0;
        this.resendTimerInterval = null;
        this.canResendOTP = true;
        this.isOtpLoading = false;
        this.loginStep = 1;
        this.organizationLoginMethods = [];
    }

    changeLoginWith() {
        this.loginWith = this.loginWith === LOGIN_WITH.EMAIL ? LOGIN_WITH.USERNAME : LOGIN_WITH.EMAIL;
        const identifierControl = this.loginForm.get('identifier');
        if (this.loginWith === LOGIN_WITH.EMAIL) {
            identifierControl?.setValidators([Validators.required, Validators.email]);
        } else {
            identifierControl?.setValidators([Validators.required]);
        }
        identifierControl?.updateValueAndValidity();
    }

    private navigateToReturnUrl(): void {
        const targetUrl = localStorage.getItem('returnUrl') || '/';
        this.router.navigateByUrl(targetUrl).finally(() => {
            localStorage.removeItem('returnUrl');
        });
    }

    private parseReturnUrl(returnUrl: string): URL | null {
        if (!returnUrl) {
            return null;
        }
        try {
            return new URL(returnUrl, window.location.origin);
        } catch (error) {
            return null;
        }
    }

    redirect(path: string) {
        this.router.navigate([path]);
    }
}

